import { type JSX, useState } from "react"

// What to do about a workspace whose compute is a process on this machine.
//
// An E2B workspace arrives ready: provisioning creates the sandbox and starts
// the runtime inside it. A local one cannot — the runtime is started by hand,
// because nothing in Rails can start a process on your laptop. So the row says
// `running` while nothing is listening, and the only thing standing between
// this page and a working workspace is a command in a terminal. This pane is
// that command.

type LocalInstructionsProps = {
  // Database id, not the public one: bin/local-runtime takes the row's id.
  workspaceDbId: string
  // The address this sandbox owns. Blank on rows written before sandboxes
  // carried their own, which fall back to PARATELLIGENT_RUNTIME_URL.
  runtimeUrl: string
  // Where this workspace's files live on disk — the same path the runtime is
  // handed, computed server-side so the two cannot disagree.
  runtimeDir: string
}

export function LocalInstructions({ workspaceDbId, runtimeUrl, runtimeDir }: LocalInstructionsProps): JSX.Element {
  const runtimeCommand = `PARATELLIGENT_RESEARCHER_MODEL=codex-gpt-5.6 bin/local-runtime ${workspaceDbId}`

  return (
    <article className="workspace__doc">
      <header className="workspace__doc-head">
        <h1 className="workspace__doc-title">Local instructions</h1>
        <p className="workspace__doc-meta">
          This workspace runs on your machine instead of E2B. Its runtime is a process you start
          yourself — until you do, the page works but every command fails to connect.
        </p>
      </header>

      <div className="workspace__steps">
        <Step
          number={1}
          title="Start the runtime"
          hint="Runs in the foreground: its logs are in that terminal, and Ctrl-C stops the workspace. Leave it running while you work."
          command={runtimeCommand}
        />

        <Step
          number={2}
          title="Start the gateway"
          hint="Only for agents that call models — echo agents need nothing. It has to share GATEWAY_SIGNING_SECRET with this app, and the local registry because that is the only one carrying the codex- ids step 1 asks for."
          command="cd ../paratelligent_gateway && REGISTRY_PATH=registry.local.json go run ./cmd/gateway"
        />
      </div>

      <section className="workspace__field">
        <p className="workspace__field-label">Where things are</p>
        <p className="workspace__field-hint">
          Commands go to <code className="workspace__code">{runtimeUrl || "PARATELLIGENT_RUNTIME_URL"}</code>
          {runtimeUrl
            ? " — this sandbox's own port, so other local workspaces can run alongside it."
            : " — this sandbox predates per-sandbox ports, so it uses the shared address from .env."}
        </p>
        <p className="workspace__field-hint">
          Files live in <code className="workspace__code">{runtimeDir}</code>, which is where{" "}
          <code className="workspace__code">shared/objective.md</code> and the agent directories appear. The
          path is stable, so stopping and restarting the runtime picks up the same work. Move the root
          with <code className="workspace__code">PARATELLIGENT_WORKSPACE_DIR</code> in{" "}
          <code className="workspace__code">.env</code>.
        </p>
      </section>
    </article>
  )
}

function Step({
  number,
  title,
  hint,
  command,
}: {
  number: number
  title: string
  hint: string
  command: string
}): JSX.Element {
  return (
    <section className="workspace__step">
      <p className="workspace__step-title">
        <span className="workspace__step-number">{number}</span>
        {title}
      </p>
      <p className="workspace__field-hint">{hint}</p>
      <CommandLine command={command} />
    </section>
  )
}

// The command, and a button that puts it on the clipboard. Copying can be
// refused (an insecure origin, a denied permission), so the button says what
// happened rather than pretending.
function CommandLine({ command }: { command: string }): JSX.Element {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle")

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(command)
      setState("copied")
    } catch {
      setState("failed")
    }
    setTimeout(() => setState("idle"), 2000)
  }

  return (
    <div className="workspace__command">
      <code className="workspace__command-text">{command}</code>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void copy()}>
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
      </button>
    </div>
  )
}
